import { Box, Grid, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { T } from "react-translator-component";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { colors } from "../../../common/colors";
import { generations, prefectures } from "../../constants/statistics";
import apiServices from "../../services/apiServices";
import { toDisplayedTerm } from "../../util/statistics";
import { formatNumber } from "../../util/util";
import DropDown from "../DropDown"
import Loading from "../Loading";
import CollapseItem from "./CollapseItem";
import Divider from "./Divider"
import AggregationButton from "./AggregationButton";
import Term, { TermRef } from "../Term"
import { styles as baseStyle } from "./styles"

interface Props {
  baseDate: Dayjs
  initialized: () => void
  handleError: (error: any) => void
}

const UsersUtilization: React.FC<Props> = ({ baseDate, initialized, handleError }: Props) => {
  /* 入力データ用 */
  const totallingTermRef = useRef<TermRef>(null);
  const tagsTermRef = useRef<TermRef>(null);

  const labelOptions = [
    {value: 'generation', name: '年代別'},
    {value: 'prefecture', name: '地域別'}
  ];
  const [label, setLabel] = useState<string>('generation');

  /* 出力データ用 */
  const [totallingTermData, setTotallingTermData] = useState<any>();
  const [totallingRowData, settotallingRowData] = useState<any>();
  const [tagsData, setTagsData] = useState<any>();

  /* 表示制御用 */
  const maxCountTag = 3;
  const [loadingTotallingData, setLoadingTotallingData] = useState<boolean>(false);
  const [loadingTagsData, setLoadingTagsData] = useState<boolean>(false);

  const chartData = useMemo(() => {
    if (!totallingRowData) {
      return []
    }
    const {labels, data} = label === 'generation'
      ? {labels: generations, data: totallingRowData.per_generation}
      : {labels: prefectures, data: totallingRowData.per_prefecture};
    return labels.map((key: string) => {
      return {
        label: key,
        posts: data.posts[key] ?? 0,
        comments: data.comments[key] ?? 0,
        likes: data.likes[key] ?? 0
      }
    })
  }, [totallingRowData, label]);

  const axisDummyData = useMemo(() => {
    if (!chartData || chartData.length < 1) {
      return [{label: '', posts: 100, comments: 100, likes: 100}]
    }
    return [{
      label: '',
      posts: chartData.reduce((max: number, item: any) => Math.max(max, item.posts), 0),
      comments: chartData.reduce((max: number, item: any) => Math.max(max, item.comments), 0),
      likes: chartData.reduce((max: number, item: any) => Math.max(max, item.likes), 0),
    }]
  }, [chartData]);

  useEffect(() => {
      initialize();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
  const initialize = async () => {
    try {
      await Promise.all([
        fetchTotallingData(baseDate, baseDate),
        fetchTagsData(baseDate, baseDate),
      ]);
    } catch (error: any) {
      handleError(error);
    } finally {
      initialized();
    }
  }

  const fetchTotallingData = async (_startDate: Dayjs, _endDate: Dayjs) => {
    const res = await apiServices.getStatisticsUserUtilizationTotalling(_startDate, _endDate);
    setTotallingTermData(res.term);
    settotallingRowData(res.utilization);
  }

  const fetchTagsData = async (_startDate: Dayjs, _endDate: Dayjs) => {
    const res = await apiServices.getStatisticsUserUtilizationTags(_startDate, _endDate, maxCountTag);
    setTagsData(res);
  }

  /* UI 操作関連 */
  const changeLabel = (value?: string) => {
    if (!value) {
      return
    }
    setLabel(value);
  }

  const submitTotalling = async () => {
    if (!totallingTermRef || !totallingTermRef.current) {
      return false
    }

    try {
      setLoadingTotallingData(true);
      const term = totallingTermRef.current.getTerm();
      await fetchTotallingData(term.startDate, term.endDate);
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoadingTotallingData(false);
    }
    return false
  }

  const submitTags = async () => {
    if (!tagsTermRef || !tagsTermRef.current) {
      return false
    }

    try {
      setLoadingTagsData(true);
      const term = tagsTermRef.current.getTerm();
      await fetchTagsData(term.startDate, term.endDate);
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoadingTagsData(false);
    }
    return false
  }

  /* グラフ表示関連 */

  const convertTooltipLabel = (key: string) => {
    const item = chartData.find((data: any) => data.label === key);
    return item ? translateLabel(key) : '';
  }

  const translateLabel = (key: string) => {
    return label === 'generation' ? T(`mng_users_utilization_generation_${key.replace('-', '_')}`) : key;
  }

  return (
    <CollapseItem title={T('mng_utilization')}>
      <Box sx={styles.container}>
        <Typography sx={baseStyle.dataTitle}>
          {T('mng_users_utilization_posts')} {T('mng_users_utilization_comments')} {T('mng_users_utilization_likes')}
        </Typography>
        <Box sx={styles.totallingConditionContainer}>
          <Typography sx={styles.totallingConditionTermTitle}>{T('mng_term')}</Typography>
          <Term
            baseDate={baseDate}
            ref={totallingTermRef}
          />
          <AggregationButton
            sx={styles.totallingConditionButton}
            onSubmit={submitTotalling}
            validate={() => Promise.resolve(totallingTermRef.current?.validate() ?? false)}
          />
        </Box>
        <Box sx={styles.totallingTermDataContainer}>
          <Box sx={styles.totallingSettingsContainer}>
            <Typography>
              {toDisplayedTerm(totallingTermData?.start_date, totallingTermData?.end_date)}
            </Typography>
            <DropDown 
              title={T('mng_users_utilization_unit')}
              value={label}
              options={labelOptions}
              onChange={(e) => changeLabel(e.target.value)}
            />
          </Box>
          {
            <Box sx={styles.totallingResultContainer}>
              <Loading visible={loadingTotallingData} />
              <Box
                sx={{ ...styles.barChartContainer, maxHeight: 400 }}
              >
                <ResponsiveContainer width="100%" height={chartData.length * 40}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    width={600}
                    margin={{ left: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="10 0"
                      horizontal={false}
                      vertical={true}
                    />
                    <XAxis type="number" hide={true} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={200}
                      tickFormatter={(key: string) => translateLabel(key)}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [formatNumber(value), `${name}`]}
                      labelFormatter={convertTooltipLabel}
                    />
                    <Bar dataKey="posts" fill={colors.farm_blue200} name={T('mng_users_utilization_posts')} />
                    <Bar dataKey="comments" fill={colors.farm_red600} name={T('mng_users_utilization_comments')} />
                    <Bar dataKey="likes" fill={colors.farm_green500} name={T('mng_users_utilization_likes')} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <Box
                sx={{ ...styles.barChartContainer, height: 60 }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={axisDummyData}
                    layout="vertical"
                    width={600}
                    margin={{ left: 0 }}
                  >
                    <XAxis type="number" />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={200}
                      height={0}
                      stroke="transparent"
                    />
                    <Legend
                      payload={[
                        {value: T('mng_users_utilization_posts'), type: "square", id: "posts", color: colors.farm_blue200},
                        {value: T('mng_users_utilization_comments'), type: "square", id: "posts", color: colors.farm_red600},
                        {value: T('mng_users_utilization_likes'), type: "square", id: "posts", color: colors.farm_green500}
                      ]}
                    />
                    <Bar dataKey="posts" fill="transparent" />
                    <Bar dataKey="comments" fill="transparent" />
                    <Bar dataKey="likes" fill="transparent" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          }
        </Box>

        <Divider />

        <Typography sx={baseStyle.dataTitle}>{T('mng_users_utilization_tags')}</Typography>
        <Grid container sx={styles.tagsContainer}>
          <Grid item xs={4} sx={styles.tagConditions}>
            <Typography>{T('mng_term')}</Typography>
            <Term
              baseDate={baseDate}
              vertical={true}
              ref={tagsTermRef}
            />
            <AggregationButton
              onSubmit={submitTags}
              validate={() => Promise.resolve(tagsTermRef.current?.validate() ?? false)}
            />
          </Grid>
          <Grid item xs={8} sx={styles.tags}>
            { tagsData?.utilization &&
              <>
                <Typography sx={baseStyle.dataTitle}>
                  {toDisplayedTerm(tagsData.term.start_date, tagsData.term.end_date)}
                </Typography>
                {
                  tagsData.utilization.tags.length > 0 ?
                  (
                  <Grid container component={"dl"}>
                    <Loading visible={loadingTagsData} />
                    {Array.from({length: maxCountTag }).map((_, index: number) => (
                      <React.Fragment key={index}>
                        <Grid item component={"dt"} xs={3}>{ index + 1 }</Grid>
                        <Grid item component={"dd"} xs={9}>{ index < tagsData.utilization.tags.length ? tagsData.utilization.tags[index].tag  : ''}</Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                  ) : (
                    <Box sx={styles.tagsContainerNoData}>{T('mng_statistics_no_post_tags')}</Box>
                  )
                }
                
              </>
            }
          </Grid>
        </Grid>
      </Box>
    </CollapseItem>
  )
}
export default UsersUtilization

const styles = {
  container: {
    textAlign: "left"
  },
  totallingConditionContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: 2,
  },
  totallingConditionTermTitle: {
    paddingTop: "1.5em"
  },
  totallingConditionButton: {
    paddingTop: "1.5em"
  },
  totallingTermDataContainer: {
    marginY: 3,
    marginLeft: 1
  },
  totallingSettingsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 1
  },
  totallingResultContainer: {
    position: "relative"
  },
  barChartContainer: {
    overflowY: "auto",
    px: 2,
    position: "relative",
    scrollbarGutter: "stable"
  },
  tagsContainer: {
    "& dl": {
      borderTop: "1px solid #DCDCDC",
      borderRight: "1px solid #DCDCDC",
      position: "relative"
    },
    "& dt": {
      borderLeft: "1px solid #DCDCDC",
      borderBottom: "1px solid #DCDCDC",
      fontWeight: "normal",
      padding: 1,
      textAlign: "center"
    },
    "& dd": {
      borderLeft: "1px solid #DCDCDC",
      borderBottom: "1px solid #DCDCDC",
      padding: 1      
    }
  },
  tagConditions: {
    padding: 1
  },
  tags: {
    padding: 2
  },
  tagsContainerNoData: {
    display: "flex",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  }
}
